import React, { useCallback, useEffect, useState } from "react";
import { WifiSlash, WifiHigh, CloudArrowUp, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Modal, Btn } from "./Shared";
import * as offline from "../lib/offline";
import { syncQueue, discardOp } from "../lib/sync";

export function useOfflineState() {
  const [online, setOnline] = useState(() => !offline.isOffline());
  const [queue, setQueue] = useState([]);
  const refresh = useCallback(() => offline.queueList().then(setQueue), []);
  useEffect(() => {
    const on = () => { setOnline(true); refresh(); };
    const off = () => { setOnline(false); refresh(); };
    window.addEventListener("online", on); window.addEventListener("offline", off);
    const unsub = offline.onOfflineChange(refresh);
    refresh();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); unsub(); };
  }, [refresh]);
  return { online, queue, refresh };
}

const fmt = (s) => (s || "").slice(0, 16).replace("T", " ");

function ConflictsModal({ open, onClose, conflicts, errors = [], onResolve, busy }) {
  return (
    <Modal open={open} onClose={onClose} title="Conflictos al sincronizar" wide>
      <div className="space-y-3" data-testid="sync-conflicts-modal">
        <p className="text-sm text-slate-600">{conflicts.length ? "Estos elementos ya fueron revisados por otra persona en la misma revisión general mientras trabajabas sin cobertura. Decide qué revisión se conserva." : "Estas revisiones no se pudieron registrar en el servidor."}</p>
        {conflicts.map((c) => (
          <div key={c.op_id} className="border border-amber-300 bg-amber-50/50 rounded-sm p-3" data-testid={`sync-conflict-${c.element_id}`}>
            <div className="font-semibold text-slate-900">{c.payload?.denominacion || c.conflict?.element_denominacion || "Elemento"}</div>
            <div className="text-xs text-slate-600 mt-1">{c.conflict?.message}</div>
            <div className="text-xs text-slate-500 mt-0.5">Tu revisión sin cobertura: {fmt(c.client_at)} · {(c.payload?.items || []).filter((i) => i.status === "ko").length} KO</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Btn variant="secondary" className="!text-xs" disabled={busy} onClick={() => onResolve(c.op_id, "discard")} data-testid={`sync-conflict-discard-${c.element_id}`}>Conservar la del servidor (descartar la mía)</Btn>
              <Btn className="!text-xs" disabled={busy} onClick={() => onResolve(c.op_id, "force")} data-testid={`sync-conflict-force-${c.element_id}`}>Sobrescribir con mi revisión</Btn>
            </div>
          </div>
        ))}
        {errors.map((c) => (
          <div key={c.op_id} className="border border-red-300 bg-red-50/50 rounded-sm p-3" data-testid={`sync-error-${c.element_id}`}>
            <div className="font-semibold text-slate-900">{c.payload?.denominacion || "Elemento"}</div>
            <div className="text-xs text-[#C40016] mt-1">No se pudo registrar: {c.error}</div>
            <div className="text-xs text-slate-500 mt-0.5">Tu revisión sin cobertura: {fmt(c.client_at)}</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Btn variant="secondary" className="!text-xs" disabled={busy} onClick={() => onResolve(c.op_id, "discard")} data-testid={`sync-error-discard-${c.element_id}`}>Descartar esta revisión</Btn>
              <Btn className="!text-xs" disabled={busy} onClick={() => onResolve(c.op_id, "retry")} data-testid={`sync-error-retry-${c.element_id}`}>Reintentar</Btn>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// Barra de estado: sin conexión / revisiones pendientes de enviar / conflictos. Sincroniza sola al recuperar la red.
export function OfflineBar() {
  const { online, queue, refresh } = useOfflineState();
  const [busy, setBusy] = useState(false);
  const [showConflicts, setShowConflicts] = useState(false);
  const pending = queue.filter((q) => q.status !== "conflict" && q.status !== "error");
  const conflicts = queue.filter((q) => q.status === "conflict");
  const errors = queue.filter((q) => q.status === "error");

  const sync = useCallback(async (opts) => {
    setBusy(true);
    try {
      const r = await syncQueue(opts);
      if (r.ok) { toast.success(`${r.ok} revisión/es sin cobertura enviada/s al servidor.`); window.dispatchEvent(new CustomEvent("gmao:synced")); }
      if (r.conflicts.length) { toast.warning(`${r.conflicts.length} conflicto/s: otro operario ya revisó el elemento.`); await refresh(); setShowConflicts(true); }
      r.errors.forEach((e) => toast.error(`${e.op?.payload?.denominacion || "Revisión"}: ${e.message}`));
    } catch (e) { toast.error("No se pudo sincronizar. Se reintentará cuando haya conexión."); }
    finally { setBusy(false); refresh(); }
  }, [refresh]);

  // Al recuperar la red (o al abrir la app ya con conexión) la cola se envía sola.
  const autoRef = React.useRef(false);
  useEffect(() => {
    if (online && pending.length && !busy && !autoRef.current) { autoRef.current = true; sync().finally(() => { autoRef.current = false; }); }
  }, [online, pending.length]); // eslint-disable-line

  const resolve = async (opId, how) => {
    setBusy(true);
    try {
      if (how === "discard") { await discardOp(opId); toast.success("Revisión descartada: se conserva la del servidor."); }
      else if (how === "retry") { const r = await syncQueue({ onlyIds: [opId] }); if (r.ok) { toast.success("Revisión enviada."); window.dispatchEvent(new CustomEvent("gmao:synced")); } else if (r.errors.length) toast.error(r.errors[0].message); }
      else { const r = await syncQueue({ onlyIds: [opId], force: true }); if (r.ok) { toast.success("Revisión sobrescrita en el servidor."); window.dispatchEvent(new CustomEvent("gmao:synced")); } }
    } finally { setBusy(false); refresh(); }
  };
  const prevConf = React.useRef(0);
  useEffect(() => { if (showConflicts && prevConf.current > 0 && !conflicts.length && !errors.length) setShowConflicts(false); prevConf.current = conflicts.length + errors.length; }, [conflicts.length, errors.length, showConflicts]);

  if (online && !queue.length) return null;
  return (
    <>
      <div className={`sticky top-0 z-30 flex flex-wrap items-center gap-3 px-4 py-2 text-sm border-b ${online ? "bg-[#E1E5E9] border-slate-300 text-slate-800" : "bg-[#2F3D4C] text-white border-[#2F3D4C]"}`} data-testid="offline-bar">
        {online ? <WifiHigh size={18} /> : <WifiSlash size={18} />}
        <span className="font-semibold" data-testid="offline-status">{online ? "Conexión recuperada" : "Sin conexión · trabajando con la copia del dispositivo"}</span>
        {pending.length > 0 && <span className="inline-flex items-center gap-1 text-xs" data-testid="offline-pending-count"><CloudArrowUp size={14} /> {pending.length} revisión/es pendiente/s de enviar</span>}
        {(conflicts.length > 0 || errors.length > 0) && <button type="button" onClick={() => setShowConflicts(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#C40016] underline" data-testid="offline-conflicts-btn"><Warning size={14} /> {conflicts.length + errors.length} revisión/es por resolver</button>}
        {online && pending.length > 0 && <Btn className="!text-xs !py-1 ml-auto" disabled={busy} onClick={() => sync()} data-testid="offline-sync-btn">{busy ? "Sincronizando…" : "Sincronizar ahora"}</Btn>}
      </div>
      <ConflictsModal open={showConflicts} onClose={() => setShowConflicts(false)} conflicts={conflicts} errors={errors} onResolve={resolve} busy={busy} />
    </>
  );
}
